// TokenModal.jsx
import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { list } from "../contexts/SelectTokenDATA";
import { getCurrencyMetadata, balanceOf } from "thirdweb/extensions/erc20";
import { getErc20Contract, getWalletBalanceContract } from "../contexts/thirdWebClient";
import {
    useActiveAccount,
    useActiveWalletChain,
} from "thirdweb/react";
import { ethers } from "ethers";
import { toast } from "react-toastify";

const TokenModal = ({ open, setOpen, onClick }) => {
    const tokens = list;
    const [searchQuery, setSearchQuery] = useState("");
    const [customToken, setCustomToken] = useState(null);
    const [balances, setBalances] = useState({});
    const [balancesLoading, setBalancesLoading] = useState(false);

    const add = useActiveAccount();
    const [address, setAddress] = useState("");
    const chain = useActiveWalletChain();

    useEffect(() => {
        if (add && add.address) {
            setAddress(add.address);
        }
    }, [add, chain]);

    // Clear search when modal closes
    useEffect(() => {
        if (!open) {
            setSearchQuery("");
            setCustomToken(null);
        }
    }, [open]);

    // Fetch balances when modal opens
    useEffect(() => {
        if (open && address && chain) {
            fetchBalances();
        }
    }, [open, address, chain]);

    const fetchBalances = async () => {
        setBalancesLoading(true);
        const newBalances = {};
        for (const token of tokens) {
            try {
                if (token.address === "0x0000000000000000000000000000000000000000") {
                    const bal = await getWalletBalanceContract(address, chain);
                    newBalances[token.address] = Number(ethers.formatUnits(bal, 18));
                } else {
                    const contract = getErc20Contract(token.address, chain);
                    const bal = await balanceOf({ contract, address });
                    newBalances[token.address] = Number(ethers.formatUnits(bal, token.decimals));
                }
            } catch {
                newBalances[token.address] = 0;
            }
        }
        setBalances(newBalances);
        setBalancesLoading(false);
    };

    // Search by address for custom tokens
    const searchByAddress = async (value) => {
        try {
            if (value && value.startsWith("0x") && value.length === 42 && chain?.id && chain?.rpc) {
                const contract = getErc20Contract(value, chain);
                let currencyMetadata = await getCurrencyMetadata({ contract });
                currencyMetadata.address = value;
                setCustomToken(currencyMetadata);
            } else {
                setCustomToken(null);
            }
        } catch (error) {
            toast.error(error?.message);
            setCustomToken(null);
        }
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        // If it looks like an address, search on-chain
        if (value.startsWith("0x") && value.length > 10) {
            searchByAddress(value);
        } else {
            setCustomToken(null);
        }
    };

    const selectToken = (token) => {
        onClick(token);
        setOpen(false);
        setSearchQuery("");
        setCustomToken(null);
    };

    // Filter tokens by name or symbol
    const filteredTokens = tokens.filter((token) => {
        if (!searchQuery || searchQuery.startsWith("0x")) return true;
        const query = searchQuery.toLowerCase();
        return (
            token.name.toLowerCase().includes(query) ||
            token.symbol.toLowerCase().includes(query)
        );
    });

    return (
        <Dialog open={open} onClose={() => setOpen(!open)} maxWidth="sm" fullWidth>
            <DialogTitle>
                Select a token
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(!open)}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <IoClose />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <input
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    id="search"
                    placeholder="Search name or paste address"
                    type="text"
                    className="w-full rounded-xl py-2 px-3 outline-none border border-perpx-cyan/30 bg-perpx-dark/50 text-perpx-text-primary placeholder:text-perpx-text-muted focus:border-perpx-cyan transition-all"
                />

                {/* Custom token from address search */}
                {customToken && customToken.address && (
                    <div className="mt-3">
                        <p className="text-perpx-text-muted text-sm mb-2">Search result</p>
                        <div
                            onClick={() => selectToken(customToken)}
                            className="flex items-center justify-between p-3 rounded-xl bg-perpx-dark-secondary/50 border border-perpx-cyan/20 hover:border-perpx-cyan/40 cursor-pointer transition-all"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-perpx-cyan/20 flex items-center justify-center text-sm">
                                    ?
                                </div>
                                <div>
                                    <p className="text-perpx-text-primary font-medium">{customToken.name}</p>
                                    <p className="text-perpx-text-muted text-xs">{customToken.symbol}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Token list */}
                {!customToken && (
                    <div className="mt-3 flex flex-col gap-1">
                        <p className="text-perpx-text-muted text-sm mb-1">
                            {searchQuery ? "Results" : "Popular tokens"}
                        </p>
                        {filteredTokens.length === 0 ? (
                            <p className="text-perpx-text-muted text-sm text-center py-4">No tokens found</p>
                        ) : (
                            filteredTokens.map((token, index) => (
                                <div
                                    onClick={() => selectToken(token)}
                                    key={index}
                                    className="flex items-center justify-between p-3 rounded-xl hover:bg-perpx-dark-secondary/50 cursor-pointer transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        {token.emoji ? (
                                            <div className="w-8 h-8 rounded-full bg-perpx-cyan/20 flex items-center justify-center text-lg">
                                                {token.emoji}
                                            </div>
                                        ) : token.img ? (
                                            <img
                                                src={token.img}
                                                alt={token.name}
                                                className="w-8 h-8 rounded-full"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-perpx-cyan/20 flex items-center justify-center text-sm text-perpx-text-primary">
                                                {token.symbol?.charAt(0)}
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-perpx-text-primary font-medium">{token.name}</p>
                                            <p className="text-perpx-text-muted text-xs">{token.symbol}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        {balancesLoading ? (
                                            <div className="w-16 h-4 bg-perpx-dark-secondary/80 rounded animate-pulse"></div>
                                        ) : (
                                            <p className="text-perpx-text-primary text-sm">
                                                {balances[token.address] !== undefined
                                                    ? balances[token.address].toFixed(4)
                                                    : "—"}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TokenModal;
